import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PORTAL, usePortal } from '../portalAuth';
import { IMG } from '../components/StoreLayout';
import SITE from '../api';
import StorePhoneInput from '../components/StorePhoneInput';
import { STORE_BASE } from '../routeBase';

const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const lbl = { fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 };

export default function Profile() {
  const { customer, ready, setCustomer } = usePortal();
  const [form, setForm] = useState(null);
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm({
        company_name: customer.company || '', contact_person: customer.contact || '',
        phone: customer.phone || '', whatsapp: customer.whatsapp || '',
        address: customer.address || '', city: customer.city || '', state: customer.state || '',
        postal_code: customer.postal_code || '', country_id: customer.country_id || '', gstin: customer.gstin || '',
        tax_type: customer.tax_type || 'GST', tax_label: customer.tax_label || '',
        date_of_birth: customer.date_of_birth || '', anniversary_date: customer.anniversary_date || '',
      });
    }
  }, [customer]);
  useEffect(() => { SITE.get('/countries').then(r => setCountries(r.data.countries || [])); }, []);

  if (!ready) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Please sign in to view your profile</h2>
      <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)' }}>Sign in</Link>
    </div>
  );
  if (!form) return null;

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await PORTAL.put('/me', form);
      setCustomer(r.data.customer);
      toast.success('Profile updated');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 780, margin: '24px auto', padding: '0 24px 60px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>My Profile</h1>

      <ProfilePictureCard customer={customer} setCustomer={setCustomer} />

      <Card title="Company Details">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Company Name"><input style={inp} value={form.company_name} onChange={set('company_name')} /></Field>
          <Field label="Contact Person"><input style={inp} value={form.contact_person} onChange={set('contact_person')} /></Field>
          <Field label="Email (login — contact us to change)">
            <input style={{ ...inp, background: '#f7f7f7', color: '#888' }} value={customer.email || ''} disabled />
          </Field>
          <Field label="Mobile (login — contact us to change)">
            <input style={{ ...inp, background: '#f7f7f7', color: '#888' }} value={customer.mobile || ''} disabled />
          </Field>
          <Field label="Phone"><StorePhoneInput value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} /></Field>
          <Field label="WhatsApp"><StorePhoneInput value={form.whatsapp} onChange={v => setForm(f => ({ ...f, whatsapp: v }))} /></Field>
          <Field label="Birthday"><input type="date" style={inp} value={form.date_of_birth} onChange={set('date_of_birth')} /></Field>
          <Field label="Anniversary"><input type="date" style={inp} value={form.anniversary_date} onChange={set('anniversary_date')} /></Field>
        </div>
      </Card>

      <Card title="Address">
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Address</label>
          <textarea style={{ ...inp, resize: 'vertical' }} rows={2} value={form.address} onChange={set('address')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="City"><input style={inp} value={form.city} onChange={set('city')} /></Field>
          <Field label="State"><input style={inp} value={form.state} onChange={set('state')} /></Field>
          <Field label="Postal Code"><input style={inp} value={form.postal_code} onChange={set('postal_code')} /></Field>
          <Field label="Country">
            <select style={inp} value={form.country_id} onChange={set('country_id')}>
              <option value="">Select country…</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      <Card title="Tax Details">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Tax Type">
            <select style={inp} value={form.tax_type} onChange={set('tax_type')}>
              <option value="GST">GST (India)</option>
              <option value="VAT">VAT</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          {form.tax_type === 'Other' && (
            <Field label="Tax Name"><input style={inp} placeholder="e.g. Sales Tax ID" value={form.tax_label} onChange={set('tax_label')} /></Field>
          )}
          <Field label={form.tax_type === 'GST' ? 'GSTIN' : form.tax_type === 'VAT' ? 'VAT Number' : (form.tax_label || 'Tax Number')}>
            <input style={inp} value={form.gstin} onChange={set('gstin')} />
          </Field>
        </div>
      </Card>

      <div style={{ marginBottom: 20 }}>
        <button onClick={save} disabled={saving} className="sf-btn-outline"
          style={{ padding: '11px 30px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      <AddressesCard />
      <ContactsCard />
      <BusinessDocsCard customer={customer} setCustomer={setCustomer} />
      <PasswordCard />
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return <div><label style={lbl}>{label}</label>{children}</div>;
}

function ProfilePictureCard({ customer, setCustomer }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const upload = async e => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await PORTAL.post('/profile-picture', fd);
      setCustomer(c => ({ ...c, profile_picture: r.data.url }));
      toast.success('Profile picture updated');
    } catch (er) { toast.error(er.response?.data?.error || 'Upload failed'); }
    setBusy(false);
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 18 }}>
      <div style={{ width: 76, height: 76, borderRadius: '50%', background: '#f0f0f0', overflow: 'hidden', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #eee' }}>
        {customer.profile_picture ? (
          <img src={IMG(customer.profile_picture)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 26, fontWeight: 700, color: '#bbb' }}>{customer.company?.[0]?.toUpperCase() || '?'}</span>
        )}
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{customer.company}</div>
        <input ref={ref} type="file" accept="image/*" onChange={upload} style={{ display: 'none' }} />
        <button onClick={() => ref.current.click()} disabled={busy} className="sf-btn-outline"
          style={{ padding: '7px 16px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
          {busy ? 'Uploading…' : 'Change Profile Picture'}
        </button>
      </div>
    </div>
  );
}

function BusinessDocsCard({ customer, setCustomer }) {
  return (
    <Card title="Business Documents">
      <p style={{ fontSize: 12.5, color: '#888', marginBottom: 14 }}>
        Adding these helps our team verify your business faster when approving your wholesale pricing.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
        <DocUpload label="Shop Picture" field="shop-picture" column="shop_picture" customer={customer} setCustomer={setCustomer} />
        <DocUpload label="Business Visiting Card" field="visiting-card" column="visiting_card" customer={customer} setCustomer={setCustomer} />
        <DocUpload label="Business License" field="business-license" column="business_license" customer={customer} setCustomer={setCustomer} />
      </div>
    </Card>
  );
}

function AddressesCard() {
  const [addresses, setAddresses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = editing
  const [saving, setSaving] = useState(false);

  const load = () => PORTAL.get('/addresses').then(r => setAddresses(r.data.addresses || []));
  useEffect(() => { load(); SITE.get('/countries').then(r => setCountries(r.data.countries || [])); }, []);

  const startNew = () => setEditing({ label: 'Other', address_line: '', city: '', state: '', pincode: '', country_id: '', is_default: addresses.length === 0 });
  const startEdit = a => setEditing({ ...a });

  const saveAddr = async () => {
    setSaving(true);
    try {
      if (editing.id) await PORTAL.put(`/addresses/${editing.id}`, editing);
      else await PORTAL.post('/addresses', editing);
      toast.success('Address saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save address'); }
    setSaving(false);
  };

  const remove = async id => {
    if (!window.confirm('Delete this address?')) return;
    try { await PORTAL.delete(`/addresses/${id}`); load(); } catch (e) { toast.error('Could not delete'); }
  };

  return (
    <Card title="Delivery Addresses">
      <p style={{ fontSize: 12.5, color: '#888', marginBottom: 14 }}>
        Save multiple delivery addresses and pick one at checkout.
      </p>
      {addresses.map(a => (
        <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
          padding: '10px 0', borderTop: '1px solid #f2f2f2' }}>
          <div style={{ fontSize: 13 }}>
            <strong>{a.label}</strong> {a.is_default ? <span style={{ fontSize: 10.5, color: 'var(--sf-accent)', fontWeight: 700 }}>DEFAULT</span> : null}
            <div style={{ color: '#666', marginTop: 2 }}>
              {a.address_line}, {[a.city, a.state, a.pincode].filter(Boolean).join(', ')}{a.country_name ? `, ${a.country_name}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => startEdit(a)} style={{ background: 'none', border: 'none', color: 'var(--sf-accent)', cursor: 'pointer', fontSize: 12.5 }}>Edit</button>
            <button onClick={() => remove(a.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12.5 }}>Delete</button>
          </div>
        </div>
      ))}
      {addresses.length === 0 && <div style={{ fontSize: 13, color: '#999', padding: '6px 0' }}>No saved addresses yet.</div>}

      {editing ? (
        <div style={{ marginTop: 14, padding: 14, background: '#fafafa', borderRadius: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Field label="Label (e.g. Warehouse, Head Office)">
              <input style={inp} value={editing.label} onChange={e => setEditing(v => ({ ...v, label: e.target.value }))} />
            </Field>
            <Field label="Country">
              <select style={inp} value={editing.country_id || ''} onChange={e => setEditing(v => ({ ...v, country_id: e.target.value }))}>
                <option value="">Select country…</option>
                {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>Address</label>
            <textarea style={{ ...inp, resize: 'vertical' }} rows={2} value={editing.address_line} onChange={e => setEditing(v => ({ ...v, address_line: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Field label="City"><input style={inp} value={editing.city} onChange={e => setEditing(v => ({ ...v, city: e.target.value }))} /></Field>
            <Field label="State"><input style={inp} value={editing.state} onChange={e => setEditing(v => ({ ...v, state: e.target.value }))} /></Field>
            <Field label="Pincode"><input style={inp} value={editing.pincode} onChange={e => setEditing(v => ({ ...v, pincode: e.target.value }))} /></Field>
          </div>
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!editing.is_default} onChange={e => setEditing(v => ({ ...v, is_default: e.target.checked }))} />
            Set as default delivery address
          </label>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button onClick={saveAddr} disabled={saving} className="sf-btn-outline"
              style={{ padding: '7px 18px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save Address'}
            </button>
            <button onClick={() => setEditing(null)} style={{ padding: '7px 18px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 12.5, background: '#fff', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startNew} className="sf-btn-outline"
          style={{ marginTop: 10, padding: '7px 18px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
          + Add Address
        </button>
      )}
    </Card>
  );
}

function ContactsCard() {
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => PORTAL.get('/contacts').then(r => setContacts(r.data.contacts || []));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: '', designation: '', phone: '', email: '', is_primary: contacts.length === 0 });
  const startEdit = c => setEditing({ ...c });

  const saveContact = async () => {
    if (!editing.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (editing.id) await PORTAL.put(`/contacts/${editing.id}`, editing);
      else await PORTAL.post('/contacts', editing);
      toast.success('Contact saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save contact'); }
    setSaving(false);
  };

  const remove = async id => {
    if (!window.confirm('Delete this contact?')) return;
    try { await PORTAL.delete(`/contacts/${id}`); load(); } catch (e) { toast.error('Could not delete'); }
  };

  return (
    <Card title="Contacts & Team">
      <p style={{ fontSize: 12.5, color: '#888', marginBottom: 14 }}>
        Add extra contacts on your team — procurement, finance, or anyone else we should reach.
      </p>
      {contacts.map(c => (
        <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
          padding: '10px 0', borderTop: '1px solid #f2f2f2' }}>
          <div style={{ fontSize: 13 }}>
            <strong>{c.name}</strong>{c.designation ? ` — ${c.designation}` : ''}
            {c.is_primary ? <span style={{ fontSize: 10.5, color: 'var(--sf-accent)', fontWeight: 700, marginLeft: 6 }}>PRIMARY</span> : null}
            <div style={{ color: '#666', marginTop: 2 }}>{[c.phone, c.email].filter(Boolean).join(' · ')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => startEdit(c)} style={{ background: 'none', border: 'none', color: 'var(--sf-accent)', cursor: 'pointer', fontSize: 12.5 }}>Edit</button>
            <button onClick={() => remove(c.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12.5 }}>Delete</button>
          </div>
        </div>
      ))}
      {contacts.length === 0 && <div style={{ fontSize: 13, color: '#999', padding: '6px 0' }}>No additional contacts yet.</div>}

      {editing ? (
        <div style={{ marginTop: 14, padding: 14, background: '#fafafa', borderRadius: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <Field label="Name"><input style={inp} value={editing.name} onChange={e => setEditing(v => ({ ...v, name: e.target.value }))} /></Field>
            <Field label="Designation (e.g. Procurement Manager)"><input style={inp} value={editing.designation} onChange={e => setEditing(v => ({ ...v, designation: e.target.value }))} /></Field>
            <Field label="Phone"><input style={inp} value={editing.phone} onChange={e => setEditing(v => ({ ...v, phone: e.target.value }))} /></Field>
            <Field label="Email"><input style={inp} value={editing.email} onChange={e => setEditing(v => ({ ...v, email: e.target.value }))} /></Field>
          </div>
          <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!editing.is_primary} onChange={e => setEditing(v => ({ ...v, is_primary: e.target.checked }))} />
            Set as primary contact
          </label>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button onClick={saveContact} disabled={saving} className="sf-btn-outline"
              style={{ padding: '7px 18px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save Contact'}
            </button>
            <button onClick={() => setEditing(null)} style={{ padding: '7px 18px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 12.5, background: '#fff', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startNew} className="sf-btn-outline"
          style={{ marginTop: 10, padding: '7px 18px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
          + Add Contact
        </button>
      )}
    </Card>
  );
}

function DocUpload({ label, field, column, customer, setCustomer }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const value = customer[column];

  const upload = async e => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await PORTAL.post(`/${field}`, fd);
      setCustomer(c => ({ ...c, [column]: r.data.url }));
      toast.success(`${label} uploaded`);
    } catch (er) { toast.error(er.response?.data?.error || 'Upload failed'); }
    setBusy(false);
  };

  return (
    <div>
      <label style={lbl}>{label}</label>
      <div style={{ width: '100%', height: 130, border: '1.5px dashed #ddd', borderRadius: 10, background: '#fafafa',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 8 }}>
        {value ? <img src={IMG(value)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 12, color: '#bbb' }}>No image uploaded</span>}
      </div>
      <input ref={ref} type="file" accept="image/*" onChange={upload} style={{ display: 'none' }} />
      <button onClick={() => ref.current.click()} disabled={busy} className="sf-btn-outline"
        style={{ width: '100%', padding: '8px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
        {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
      </button>
    </div>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!current || !next) return toast.error('Please fill in both password fields');
    if (next !== confirm) return toast.error('New passwords do not match');
    setSaving(true);
    try {
      await PORTAL.post('/change-password', { current_password: current, new_password: next });
      toast.success('Password updated');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not change password'); }
    setSaving(false);
  };

  return (
    <Card title="Change Password">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <Field label="Current Password"><input type="password" style={inp} value={current} onChange={e => setCurrent(e.target.value)} /></Field>
        <Field label="New Password"><input type="password" style={inp} value={next} onChange={e => setNext(e.target.value)} /></Field>
        <Field label="Confirm New Password"><input type="password" style={inp} value={confirm} onChange={e => setConfirm(e.target.value)} /></Field>
      </div>
      <button onClick={submit} disabled={saving} className="sf-btn-outline"
        style={{ padding: '9px 22px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
        {saving ? 'Updating…' : 'Update Password'}
      </button>
    </Card>
  );
}
